import { Placement } from '@popperjs/core';
import { ThemeColors } from '@theme/foundations/colors';
import { cloneElement, ComponentType, useEffect, useState } from 'react';
import { usePopover } from '..';
import { PopoverProps } from '../Popover';

export type TooltipProps<T extends object> = {
  id?: string;
  arrow?: boolean;
  borderWidth?: number;
  borderColor?: string;
  delayHide?: number;
  delayShow?: number;
  children: JSX.Element;
  enabled?: boolean;
  placement?: Placement;
  Component: ComponentType<PopoverProps & T>;
} & T;

const Tooltip = <ContentProps extends object>({
  id,
  arrow = true,
  borderWidth,
  borderColor = ThemeColors.neutral['200'],
  delayHide = 300,
  delayShow = 200,
  children,
  enabled = true,
  placement = 'top',
  Component,
  ...openProps
}: TooltipProps<ContentProps>) => {
  const [referenceEl, setReferenceEl] = useState<HTMLElement>();
  const [tipEl, setTipEl] = useState<HTMLElement>();
  const [isVisible, setIsVisible] = useState(true);
  const [mouseInTip, setMouseInTip] = useState(false);
  const [mouseInEl, setMouseInEl] = useState(false);
  const [elFocused, setElFocused] = useState(false);

  const popover = usePopover(Component, {
    id,
    arrow,
    borderColor,
    borderWidth,
    placement,
    contentRef: setTipEl,
    referenceElement: referenceEl,
  });

  useEffect(() => {
    let tm: NodeJS.Timeout | null;

    const handleMouseEnter = () => {
      if (tm) {
        clearTimeout(tm);
        tm = null;
      }

      referenceEl?.addEventListener('mouseleave', handleMouseLeave);
      referenceEl?.addEventListener('focusout', handleMouseLeave);

      tm = setTimeout(() => {
        setMouseInEl(true);
      }, delayShow);
    };

    const handleMouseLeave = () => {
      setTimeout(() => {
        setMouseInEl(false);
      }, delayHide);
      referenceEl?.removeEventListener('mouseleave', handleMouseLeave);
    };

    const handleFocus = () => {
      setElFocused(true);
    };

    const handleFocusOut = () => {
      setElFocused(false);
    };

    const handleKeyDown = (event) => {
      const key = event.keyCode ? event.keyCode : event.which;
      if (key === 27) {
        event.stopPropagation();
        setElFocused(false);
      }
    };

    const handleTipMouseEnter = () => {
      setMouseInTip(true);
    };

    const handleTipMouseLeave = () => {
      setTimeout(() => {
        setMouseInTip(false);
      }, delayHide);
    };

    if (tipEl) {
      tipEl.addEventListener('mouseenter', handleTipMouseEnter);
      tipEl.addEventListener('mouseleave', handleTipMouseLeave);
    }

    if (referenceEl) {
      referenceEl.addEventListener('mouseenter', handleMouseEnter);
      referenceEl.addEventListener('focus', handleFocus);
      referenceEl.addEventListener('focusout', handleFocusOut);
      referenceEl.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (tipEl) {
        tipEl.removeEventListener('mouseenter', handleTipMouseEnter);
        tipEl.removeEventListener('mouseleave', handleTipMouseLeave);
      }

      if (referenceEl) {
        referenceEl.removeEventListener('mouseenter', handleMouseEnter);
        referenceEl.removeEventListener('mouseenter', handleMouseLeave);
      }
    };
  }, [referenceEl, tipEl, delayHide, delayShow]);

  useEffect(() => {
    if (elFocused) {
      if (!isVisible) {
        popover.open(openProps as ContentProps);
      }
      setIsVisible(true);
    } else if (mouseInEl || mouseInTip) {
      if (!isVisible) {
        popover.open(openProps as ContentProps);
      }
      setIsVisible(true);
    } else {
      if (isVisible) {
        popover.close();
      }
      setIsVisible(false);
    }
  }, [
    isVisible,
    mouseInEl,
    mouseInTip,
    elFocused,
    popover,
    openProps,
    setIsVisible,
  ]);

  if (!enabled) {
    return children;
  }

  return (
    <>
      {cloneElement(children, {
        ...children.props,
        'aria-describedby': 'tooltip',
        ref: setReferenceEl,
      })}
    </>
  );
};

export default Tooltip;
